import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'
import { canSeeSalary as checkSalary, canSeeCPFLevel as checkCPFLevel } from '@/types/user'
import type { UserRole } from '@/types/user'

export interface AuthUser {
  id: string
  email: string
  name: string
  avatarUrl?: string
  role: UserRole
  teamId?: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const token = ref<string | null>(localStorage.getItem('access_token'))

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isLead          = computed(() => user.value ? checkCPFLevel(user.value.role) : false)
  const isPM            = computed(() => user.value?.role === 'product_manager')
  const canSeeSalary    = computed(() => user.value ? checkSalary(user.value.role) : false)
  const canSeeCPFLevel  = computed(() => user.value ? checkCPFLevel(user.value.role) : false)

  async function initialize() {
    if (!token.value) return
    try {
      const { data } = await api.get<{ user: AuthUser }>('/users/me')
      user.value = data.user
    } catch (err: any) {
      if (err.response?.status === 401) {
        logout()
      } else {
        console.error('Error cargando perfil:', err)
      }
    }
  }

  function setToken(accessToken: string) {
    token.value = accessToken
    localStorage.setItem('access_token', accessToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
  }

  function logout() {
    user.value = null
    token.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('id_token')
    sessionStorage.removeItem('simulate_user')
    sessionStorage.removeItem('dev_user_override')
    delete api.defaults.headers.common['Authorization']
    delete api.defaults.headers.common['x-simulate-user-id']
    delete api.defaults.headers.common['x-id-token']
    window.location.href = '/login'
  }

  function loginWithGoogle() {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
      redirect_uri: import.meta.env.VITE_COGNITO_REDIRECT_URI,
      scope: 'openid email profile',
      identity_provider: 'Google',
    })
    window.location.href = `https://${import.meta.env.VITE_COGNITO_DOMAIN}/oauth2/authorize?${params}`
  }

  return { user, token, isAuthenticated, isLead, isPM, canSeeSalary, canSeeCPFLevel, initialize, setToken, logout, loginWithGoogle }
})
